<template>
  <div ref="chartElement" class="quick-line-chart" role="img" :aria-label="ariaLabel" :style="{ height }"></div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { LineChart } from 'echarts/charts'
import { GridComponent, MarkLineComponent, MarkPointComponent, TooltipComponent } from 'echarts/components'
import { init, use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'

use([LineChart, GridComponent, MarkLineComponent, MarkPointComponent, TooltipComponent, CanvasRenderer])

const props = defineProps({
  ariaLabel: {
    type: String,
    default: 'Line chart'
  },
  series: {
    type: Array,
    default: () => []
  },
  valueMode: {
    type: String,
    default: 'number'
  },
  xMin: {
    type: String,
    default: ''
  },
  xMax: {
    type: String,
    default: ''
  },
  yMin: {
    type: [Number, String],
    default: null
  },
  yMax: {
    type: [Number, String],
    default: null
  },
  showNowMarker: {
    type: Boolean,
    default: true
  },
  height: {
    type: String,
    default: '104px'
  }
})

const chartElement = ref(null)
let chart = null
let resizeObserver = null

const chartOptions = computed(() => {
  const normalizedSeries = props.series
    .filter((series) => Array.isArray(series.data) && series.data.length > 0)
    .map((series) => ({
      name: series.name,
      type: 'line',
      data: series.data.map((point) => [point.ts, chartValue(point.value), point.value]),
      showSymbol: series.showSymbol ?? false,
      symbol: 'circle',
      symbolSize: 5,
      silent: series.silent ?? false,
      smooth: 0.25,
      stack: series.stack,
      connectNulls: false,
      lineStyle: {
        color: series.color,
        type: series.lineType || 'solid',
        opacity: series.opacity ?? 1,
        width: series.lineWidth || 1.8
      },
      emphasis: {
        focus: 'series',
        showSymbol: true,
        lineStyle: {
          width: series.emphasisLineWidth || 2.2
        }
      },
      itemStyle: {
        color: series.color,
        opacity: series.opacity ?? 1
      },
      areaStyle: series.areaColor
        ? {
            color: series.areaColor,
            opacity: series.areaOpacity ?? 0.18
          }
        : undefined,
      markPoint: createEndpointMarkPoint(series),
      tooltip: series.tooltip === false ? { show: false } : undefined,
      z: series.z
    }))

  const nowMarker = createNowMarkerSeries()

  return {
    animation: false,
    backgroundColor: 'transparent',
    grid: {
      top: 8,
      right: endpointLabelGutter(),
      bottom: 24,
      left: 38,
      containLabel: false
    },
    tooltip: {
      trigger: 'axis',
      appendToBody: true,
      confine: false,
      backgroundColor: 'rgba(8, 13, 27, 0.96)',
      borderColor: 'rgba(226, 232, 240, 0.16)',
      borderWidth: 1,
      padding: [7, 9],
      textStyle: {
        color: '#e5edf9',
        fontSize: 11,
        fontWeight: 700
      },
      axisPointer: {
        type: 'line',
        lineStyle: {
          color: 'rgba(226, 232, 240, 0.28)',
          width: 1
        }
      },
      formatter: formatTooltip
    },
    xAxis: {
      type: 'time',
      boundaryGap: false,
      min: props.xMin || undefined,
      max: props.xMax || undefined,
      axisLine: {
        lineStyle: {
          color: 'rgba(203, 213, 225, 0.16)'
        }
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        color: 'rgba(203, 213, 225, 0.72)',
        fontSize: 10,
        margin: 8,
        hideOverlap: true,
        formatter: formatTime
      },
      splitNumber: 3,
      splitLine: {
        show: true,
        lineStyle: {
          color: 'rgba(203, 213, 225, 0.045)'
        }
      }
    },
    yAxis: {
      type: 'value',
      min: yAxisMin(),
      max: yAxisMax(),
      scale: !isBoundedPercentMode() && !hasExplicitYAxisBound(),
      splitNumber: hasExplicitYAxisBound() ? 3 : 2,
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        color: 'rgba(203, 213, 225, 0.72)',
        fontSize: 10,
        margin: 7,
        formatter: formatAxisValue
      },
      splitLine: {
        lineStyle: {
          color: 'rgba(203, 213, 225, 0.095)'
        }
      }
    },
    series: nowMarker ? [...normalizedSeries, nowMarker] : normalizedSeries
  }
})

watch(chartOptions, updateChart, { deep: true })

onMounted(async () => {
  await nextTick()
  if (!chartElement.value) return

  chart = init(chartElement.value, null, { renderer: 'canvas' })
  updateChart()

  resizeObserver = new ResizeObserver(() => {
    chart?.resize()
  })
  resizeObserver.observe(chartElement.value)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  chart?.dispose()
  chart = null
})

function updateChart() {
  if (!chart) return
  chart.setOption(chartOptions.value, true)
}

function createEndpointMarkPoint(series) {
  if (!series.endpointLabel || !Array.isArray(series.data) || series.data.length === 0) return undefined
  const lastPoint = series.data.at(-1)
  if (!lastPoint?.ts || !Number.isFinite(Number(lastPoint.value))) return undefined
  const labelOffset = Array.isArray(series.endpointLabelOffset) ? series.endpointLabelOffset : [0, 0]
  const fontSize = Number.isFinite(Number(series.endpointLabelFontSize)) ? Number(series.endpointLabelFontSize) : 11
  const lineHeight = Number.isFinite(Number(series.endpointLabelLineHeight)) ? Number(series.endpointLabelLineHeight) : 13

  return {
    symbol: 'circle',
    symbolSize: 8,
    data: [
      {
        coord: [lastPoint.ts, chartValue(lastPoint.value)],
        value: lastPoint.value
      }
    ],
    itemStyle: {
      color: series.color,
      borderColor: 'rgba(8, 13, 27, 0.96)',
      borderWidth: 2,
      shadowBlur: 8,
      shadowColor: series.color
    },
    label: {
      show: true,
      position: 'right',
      distance: 8,
      formatter: series.endpointLabel,
      color: series.color,
      fontSize,
      fontWeight: 900,
      lineHeight,
      offset: labelOffset,
      textBorderColor: 'rgba(3, 7, 18, 0.92)',
      textBorderWidth: 2
    },
    emphasis: {
      disabled: true
    }
  }
}

function hasEndpointLabels() {
  return props.series.some((series) => series.endpointLabel && Array.isArray(series.data) && series.data.length > 0)
}

function endpointLabelGutter() {
  if (!hasEndpointLabels()) return 8
  const labels = props.series
    .filter((series) => series.endpointLabel && Array.isArray(series.data) && series.data.length > 0)
    .map((series) => String(series.endpointLabel))
  return labels.some((label) => label.includes('\n')) ? 118 : 86
}

function formatTooltip(params = []) {
  const first = params[0]
  const dateLabel = first?.axisValue ? formatTooltipTime(first.axisValue) : ''
  const rows = params
    .slice()
    .filter((item) => !String(item.seriesName || '').startsWith('__'))
    .sort((a, b) => Number(b.value?.[1]) - Number(a.value?.[1]))
    .map((item) => {
      const displayValue = Array.isArray(item.value) && item.value.length > 2 ? item.value[2] : item.value?.[1]
      return `<div style="display:flex;align-items:center;justify-content:space-between;gap:14px;margin-top:3px;">
        <span><i style="display:inline-block;width:7px;height:7px;border-radius:999px;background:${item.color};margin-right:6px;"></i>${item.seriesName}</span>
        <strong>${formatValue(displayValue)}</strong>
      </div>`
    })
    .join('')

  return `<div><div style="color:#94a3b8;font-size:10px;margin-bottom:4px;">${dateLabel}</div>${rows}</div>`
}

function formatTime(value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const timeLabel = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: axisDurationMs() <= 8 * 60 * 60 * 1000 ? '2-digit' : undefined
  })
  if (!axisSpansMultipleDays()) return timeLabel
  const dayLabel = date.toLocaleDateString(undefined, { weekday: 'short' })
  return `${dayLabel} ${timeLabel}`
}

function formatTooltipTime(value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

function formatValue(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return ''
  if (props.valueMode === 'currency') {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: number >= 100000 ? 1 : 0
    }).format(number)
  }
  if (props.valueMode === 'percent') {
    return `${Math.round(number)}%`
  }
  if (props.valueMode === 'cents') {
    return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(number)}c`
  }
  if (props.valueMode === 'american') {
    if (number > 0) return `+${Math.round(number)}`
    return String(Math.round(number))
  }
  return new Intl.NumberFormat(undefined, { notation: 'compact' }).format(number)
}

function formatAxisValue(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return ''
  if (props.valueMode === 'american') return formatValue(americanChartValueToOdds(number))
  return formatValue(number)
}

function isBoundedPercentMode() {
  return props.valueMode === 'percent' || props.valueMode === 'cents'
}

function yAxisMin() {
  const explicitMin = toFiniteNumber(props.yMin)
  if (Number.isFinite(explicitMin)) return chartValue(explicitMin)
  return isBoundedPercentMode() ? 0 : undefined
}

function yAxisMax() {
  const explicitMax = toFiniteNumber(props.yMax)
  if (Number.isFinite(explicitMax)) return chartValue(explicitMax)
  return isBoundedPercentMode() ? 100 : undefined
}

function hasExplicitYAxisBound() {
  return Number.isFinite(toFiniteNumber(props.yMin)) || Number.isFinite(toFiniteNumber(props.yMax))
}

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === '') return NaN
  const number = Number(value)
  return Number.isFinite(number) ? number : NaN
}

function chartValue(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return value
  if (props.valueMode !== 'american') return number
  return americanOddsToChartValue(number)
}

function americanOddsToChartValue(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return NaN
  if (number >= 100) return number - 100
  if (number <= -100) return number + 100
  return 0
}

function americanChartValueToOdds(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return NaN
  return number >= 0 ? number + 100 : number - 100
}

function axisSpansMultipleDays() {
  const duration = axisDurationMs()
  const min = new Date(props.xMin)
  const max = new Date(props.xMax)
  if (Number.isNaN(min.getTime()) || Number.isNaN(max.getTime())) return false
  return min.toDateString() !== max.toDateString() || duration > 20 * 60 * 60 * 1000
}

function axisDurationMs() {
  const min = new Date(props.xMin).getTime()
  const max = new Date(props.xMax).getTime()
  if (!Number.isFinite(min) || !Number.isFinite(max)) return 0
  return Math.max(0, max - min)
}

function createNowMarkerSeries() {
  if (!props.showNowMarker || !props.xMin || !props.xMax) return null
  const min = new Date(props.xMin).getTime()
  const max = new Date(props.xMax).getTime()
  const now = Date.now()
  if (!Number.isFinite(min) || !Number.isFinite(max) || now < min || now > max) return null

  return {
    name: '__now-marker',
    type: 'line',
    data: [],
    silent: true,
    symbol: 'none',
    markLine: {
      symbol: 'none',
      label: {
        formatter: 'Now',
        color: 'rgba(248, 250, 252, 0.78)',
        fontSize: 10,
        fontWeight: 800,
        position: 'insideEndTop'
      },
      lineStyle: {
        color: 'rgba(248, 250, 252, 0.42)',
        type: 'dashed',
        width: 1
      },
      data: [{ xAxis: new Date(now).toISOString() }]
    },
    z: 10
  }
}
</script>

<style scoped>
.quick-line-chart {
  width: 100%;
  height: 104px;
  min-width: 0;
}
</style>
